// O Mundo de Vésper como dados + a árvore Astro → Locais → Facções → Personagens. Módulo puro (`@ecliptica/game-ui/world`).
// Endereços: /world · /world/<astro> · /world/<astro>/<local> · /world/<astro>/<local>/<facção> · /world/characters/<personagem>.
import { ECLIPSE } from "./eclipse";
import { MOON } from "./moon";
import { ASTROS } from "./story";
import { SUN } from "./sun";
import { WORLD_ASTROS, type Astro, type Character, type Faction, type Place, type WorldAstro } from "./types";

export * from "./types";
export { ASTROS, WORLD_STORY, WORLD_SUMMARY, WORLD_TAGLINE, WORLD_TITLE } from "./story";

const PARTS = [SUN, MOON, ECLIPSE];
export const PLACES: Place[] = PARTS.flatMap((p) => p.places);
export const FACTIONS: Faction[] = PARTS.flatMap((p) => p.factions);
export const CHARACTERS: Character[] = PARTS.flatMap((p) => p.characters);

/** Segmento reservado para as personagens (/world/characters/<id>). */
export const CHARACTERS_SEGMENT = "characters";

export const astroById = (id: string): Astro | undefined => ASTROS.find((a) => a.id === id);
export const placeById = (id: string) => PLACES.find((p) => p.id === id);
export const factionById = (id: string) => FACTIONS.find((f) => f.id === id);
export const characterById = (id: string) => CHARACTERS.find((c) => c.id === id);

export const placesOf = (astro: WorldAstro) => PLACES.filter((p) => p.astro === astro);
export const factionsOf = (place: string) => FACTIONS.filter((f) => f.place === place);
export const charactersOf = (faction: string) => CHARACTERS.filter((c) => c.faction === faction);

/** O que está selecionado (os antepassados vêm sempre preenchidos). Tudo null = a história global. */
export type WorldSelection = { astro: Astro | null; place: Place | null; faction: Faction | null; character: Character | null };
export const EMPTY_SELECTION: WorldSelection = { astro: null, place: null, faction: null, character: null };

/** Nó mais fundo da seleção (null = o mundo inteiro). */
export type WorldNode = Astro | Place | Faction | Character;
export function nodeOf(s: WorldSelection): WorldNode | null {
  return s.character ?? s.faction ?? s.place ?? s.astro;
}

/** Seleção a partir de uma facção (sobe até ao astro). */
function fromFaction(f: Faction): WorldSelection {
  const place = placeById(f.place)!;
  return { astro: astroById(place.astro)!, place, faction: f, character: null };
}

/** Segmentos do endereço → seleção (null = não existe). */
export function resolveWorld(segments: readonly string[]): WorldSelection | null {
  const [a, b, c, extra] = segments;
  if (a === undefined) return EMPTY_SELECTION;
  if (a === CHARACTERS_SEGMENT) {
    const ch = b && !c ? characterById(b) : undefined;
    return ch ? { ...fromFaction(factionById(ch.faction)!), character: ch } : null;
  }
  const astro = astroById(a);
  if (!astro || extra !== undefined) return null;
  if (b === undefined) return { ...EMPTY_SELECTION, astro };
  const place = placeById(b);
  if (!place || place.astro !== astro.id) return null;
  if (c === undefined) return { astro, place, faction: null, character: null };
  const faction = factionById(c);
  return faction && faction.place === place.id ? { astro, place, faction, character: null } : null;
}

/** Seleção → segmentos do endereço (o contrário de `resolveWorld`). */
export function selectionSegments(s: WorldSelection): string[] {
  if (s.character) return [CHARACTERS_SEGMENT, s.character.id];
  return [s.astro?.id, s.place?.id, s.faction?.id].filter((x): x is string => !!x);
}

/** Seleção de um nó qualquer (para montar ligações). */
export function selectionOf(node: WorldNode): WorldSelection {
  if ("symbol" in node) return { ...EMPTY_SELECTION, astro: node };
  if ("kind" in node) return { astro: astroById(node.astro)!, place: node, faction: null, character: null };
  if ("faction" in node) return { ...fromFaction(factionById(node.faction)!), character: node };
  return fromFaction(node);
}

/** Todas as páginas do Mundo, sem a raiz (para o pré-render e o sitemap). */
export function allWorldSegments(): string[][] {
  return [
    ...WORLD_ASTROS.map((a) => [a]),
    ...PLACES.map((p) => [p.astro, p.id]),
    ...FACTIONS.map((f) => [placeById(f.place)!.astro, f.place, f.id]),
    ...CHARACTERS.map((c) => [CHARACTERS_SEGMENT, c.id]),
  ];
}

/** Ficheiros de imagem de uma personagem (em apps/site/public/world-art): ficha inteira e miniatura quadrada. */
export const characterImage = (id: string) => `/world-art/${id}.webp`;
export const characterThumb = (id: string) => `/world-art/${id}-thumb.webp`;
